"use client";

import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

const SPEC_KEYS = [
	{ labelKey: "maxPartSize" },
	{ labelKey: "minPartSize" },
	{ labelKey: "generalTolerances" },
	{ labelKey: "leadTime" },
];

/** 多列表格行配置，不传则用默认 SPEC_KEYS（4 行）；columnCount 为 2 时仅渲染 Col1、Col2（如 Advantages/Disadvantages） */
export interface CapabilitiesTableRow {
	labelKey: string;
}

interface CapabilitiesProps {
	translationNamespace?: string;
	multiColumn?: boolean;
	/** 使用 titleLine1/titleLine2 两行标题，并将装饰线放在第二行右侧 */
	titleLineKeys?: boolean;
	/** 两行标题时，将装饰线放到标题下方而不是第二行右侧 */
	titleDecorationBelow?: boolean;
	/** 是否显示标题右侧描述文案 */
	showDescription?: boolean;
	titleClassName?: string;
	/** 多列表格行 key 列表，不传则用默认 4 行（maxPartSize 等） */
	rowKeys?: CapabilitiesTableRow[];
	/** 多列表格数据列数：2 或 3；为 2 时表头与单元格只显示 col1Title/Col1、col2Title/Col2 */
	columnCount?: 2 | 3;
	className?: string;
}

export default function Capabilities({
	translationNamespace = "fiveAxisCapabilities",
	multiColumn = false,
	titleLineKeys = false,
	titleDecorationBelow = false,
	showDescription = true,
	titleClassName,
	rowKeys = SPEC_KEYS,
	columnCount = 3,
	className,
}: CapabilitiesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className={cn("w-full bg-[#f5f9e8] py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				<div
					className={cn(
						"flex flex-col gap-6",
						showDescription && "lg:flex-row lg:items-end lg:gap-8 2xl:gap-x-44",
					)}
				>
					{titleLineKeys ? (
						<div className="flex flex-shrink-0 flex-col items-start">
							<h2
								className={cn(
									"max-w-[280px] text-2xl font-bold leading-tight text-black sm:max-w-[320px] sm:text-[30px] md:max-w-[550px] md:text-4xl",
									titleClassName,
								)}
							>
								<span className="block">{t("titleLine1")}</span>
								<span className="flex items-end gap-4">
									<span>{t("titleLine2")}</span>
									{!titleDecorationBelow && (
										<span className="mb-1.5 h-1 w-12 flex-shrink-0 bg-themePrimary400 sm:mb-2 md:w-24" />
									)}
								</span>
							</h2>
							{titleDecorationBelow && (
								<span className="mt-4 h-1 w-24 flex-shrink-0 bg-themePrimary400 sm:mt-5 md:w-28" />
							)}
						</div>
					) : (
						<div className="flex flex-shrink-0 items-end gap-4">
							<h2
								className={cn(
									"max-w-[280px] text-2xl font-bold leading-tight text-black sm:max-w-[320px] sm:text-[30px] md:max-w-[450px] md:text-4xl",
									titleClassName,
								)}
							>
								{t("title")}
							</h2>
							<div className="mb-1.5 h-1 w-12 flex-shrink-0 bg-themePrimary400 sm:mb-2 md:w-24" />
						</div>
					)}

					{showDescription && (
						<div className="flex-1">
							<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
						</div>
					)}
				</div>

				<div className="mt-6 sm:mt-8 md:mt-10 lg:mt-12">
					{multiColumn ? (
						<div className="w-full overflow-x-auto">
							<table className="w-full min-w-[600px]">
								<thead>
									<tr className="border-b border-gray-300">
										<th className="w-[25%] pb-6 text-left text-lg font-bold text-black md:text-xl"></th>
										<th className="w-[25%] pb-6 text-left text-lg font-bold text-black md:text-xl">
											{t("col1Title")}
										</th>
										<th className="w-[25%] pb-6 text-left text-lg font-bold text-black md:text-xl">
											{t("col2Title")}
										</th>
										{columnCount >= 3 && (
											<th className="w-[25%] pb-6 text-left text-lg font-bold text-black md:text-xl">
												{t("col3Title")}
											</th>
										)}
									</tr>
								</thead>
								<tbody>
									{rowKeys.map((spec) => (
										<tr key={spec.labelKey} className="border-t border-gray-300">
											<td className="py-5 pr-4 text-sm font-bold text-black md:py-6 md:text-base">
												{t(spec.labelKey)}
											</td>
											<td className="py-5 pr-4 text-sm text-black md:py-6 md:text-base">
												{t(`${spec.labelKey}Col1`)}
											</td>
											<td className="py-5 pr-4 text-sm text-black md:py-6 md:text-base">
												{t(`${spec.labelKey}Col2`)}
											</td>
											{columnCount >= 3 && (
												<td className="py-5 text-sm text-black md:py-6 md:text-base">
													{t(`${spec.labelKey}Col3`)}
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<div className="w-full">
							<div className="grid grid-cols-[45%_55%] pb-6">
								<div></div>
								<h3 className="text-lg font-bold text-black md:text-xl">{t("specificationsTitle")}</h3>
							</div>

							{rowKeys.map((spec) => (
								<div
									key={spec.labelKey}
									className="grid grid-cols-[45%_55%] border-t border-gray-300 py-5 md:py-6"
								>
									<div className="text-lg font-bold text-black md:pl-8 md:text-xl lg:px-12 2xl:pl-44">
										{t(spec.labelKey)}
									</div>
									<div className="text-sm text-black md:text-base">{t(`${spec.labelKey}Value`)}</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
